import React, { useEffect, useMemo, useRef, useState } from 'react';
import { colors } from '../../../../style-utils/colors';
import { Text } from '../../../../style-utils/text';
import {
  Presence,
  Staleable,
  StateSwitch,
  overlayMotion,
  scrimMotion,
  useOverlayMotion,
} from '../../../../motion';
/* WP-37 — the shared table engine. This screen had no table element at all, and
   its "Action" header was ellipsised to "Ac..." in a 6% column with no title and
   no other exposure of the word (MR-23). The engine's Th wraps instead. */
import {
  BodyRow,
  CellFlex,
  Table,
  TableCaption,
  TableFrame,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '../../../table/list-table';
import {
  ActionModal,
  BuildingModalCard,
  BuildingNameButton,
  BuildingNameStack,
  BuildingPortfolioSubtitle,
  BuildingsListing,
  ButtonContainer,
  ButtonTitle,
  CancelButton,
  Column,
  CoverImage,
  CoverSlot,
  DashboardContent,
  EmptyState,
  EmptyStateIcon,
  ModalScrim,
  NewBuildingButton,
  PortfolioFilterContainer,
  RestoreBuildingButton,
  RowActionTrigger,
  StyledBuildingSelect,
  Title,
  ListingNotice,
} from './dashboard-buildings.styled';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ThreeDots } from '../../../icons/three-dots';
import { ModalPortal } from '../../../modal/modal-portal';
import { useDialogFocus } from '../../../hooks/use-dialog-focus';
import { DashboardEditBuildingById } from './dashboard-edit-building-by-id';
import { PaginationButtons } from '../dashboard-shared-components/table-pagination/table-pagination';
import { usePagination } from '../../../hooks/use-pagination';
import { usePortfolios } from '../../../hooks/use-portfolios';
import { BuildingData } from '../../../../interfaces/building-interfaces';
import {
  createItem,
  fetchAllItems,
  updatePropertyOrUserItem,
} from '../../../requests/requests';
import { User } from '../../../../interfaces/user-interfaces';
import { useAuth } from '../../../../context/auth-provider';
import { deskInPlan } from '../../../../utils/products';
import { useResource } from '../../../hooks/use-resource';
import {
  FormSkeleton,
  ListSkeleton,
  LoadError,
  formSkeletonHeight,
  skeletonHeight,
} from '../dashboard-shared-components/load-state';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import { ROLES, normalizeRole } from '../../../../auth/roles';
import { ListingControls, ListingSearch } from '../dashboard.styled';
import { StyledInput } from '../../../login/login.styled';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import toast from '../../../../utils/toast';

// Front-desk staffing codes -> friendly labels for the buildings list.
const STAFFING_LABELS: { [code: string]: string } = {
  '24_7': '24/7 concierge',
  daytime: 'Daytime concierge',
  part_time: 'Part-time / shared',
  unstaffed: 'Unstaffed',
};

// The building being viewed lives in the URL as '?edit=<id>' — so a refresh
// reopens the same property, a deep link works, and Back closes the modal.
//
// It is a QUERY param rather than a path segment for two concrete reasons.
// dashboard.tsx routes on a pathname SWITCH STATEMENT, so a path variant needs a
// new case and a parser; and dashboard.tsx resets the content scroller on every
// pathname change, so a path variant would jump the list to the top behind the
// modal and again when it closed.
const EDIT_PARAM = 'edit';

/* The list's rows are the table's own height; the skeleton reserves it. */
const SKELETON_ROWS = 4;
// Measured on the rendered table at 1366px: name + portfolio subtitle rows.
const ROW_HEIGHT = 76;

/**
 * The building's COVER photo in its row — the entity_images row flagged
 * is_primary, presigned by the server as `cover_url`.
 *
 * THE BOX IS ALWAYS THERE. This used to render the <img> only when there was a
 * URL, so a row without a photo was 24px narrower in its first cell and the
 * table reflowed as covers arrived. Now the slot is reserved either way — a
 * tinted square stands in when there is no photo — and the image itself fades
 * in only once it has decoded, so a slow presigned URL never paints a broken
 * glyph or pops in a frame late.
 */
const CoverThumb: React.FC<{ src?: string | null }> = ({ src }) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <CoverSlot aria-hidden="true">
      {src ? (
        <CoverImage
          src={src}
          alt=""
          loading="lazy"
          initial={{ opacity: 0 }}
          animate={{ opacity: loaded ? 1 : 0 }}
          onLoad={() => setLoaded(true)}
          // A cached image can be complete before React attaches onLoad.
          ref={(node: HTMLImageElement | null) => {
            if (node && node.complete && node.naturalWidth > 0) {
              setLoaded(true);
            }
          }}
        />
      ) : null}
    </CoverSlot>
  );
};

/**
 * A deep link that arrived before the list did. The skeleton goes INSIDE the
 * modal — closing it and reopening once the rows land would flicker the deep
 * link shut in front of the user. Same card, same size, so nothing jumps when
 * the form replaces the skeleton. It is on the dialog stack like the form it
 * precedes (Escape closes it, focus is held), and both scrim and card carry
 * the modal motion so the caller's Presence can play their exit.
 */
const BuildingLoadingDialog: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  const dialogRef = useDialogFocus<HTMLDivElement>(true, onClose);
  const cardMotion = useOverlayMotion('modal');

  return (
    <ModalPortal>
      <ModalScrim
        {...scrimMotion}
        onMouseDown={event => {
          if (event.target === event.currentTarget) onClose();
        }}
      >
        <BuildingModalCard
          {...cardMotion}
          ref={dialogRef}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-label="Loading building"
          aria-busy="true"
          style={{ padding: '24px', minHeight: formSkeletonHeight(5) }}
          onMouseDown={event => event.stopPropagation()}
        >
          <FormSkeleton fields={5} label="building" />
        </BuildingModalCard>
      </ModalScrim>
    </ModalPortal>
  );
};

export const DashboardBuildings: React.FC = () => {
  const {
    user,
    permissions,
  }: { user: User | null | undefined; permissions: string[] } = useAuth();
  const desk = deskInPlan(user);
  const canCreateBuilding = can(permissions, PERMISSIONS.BUILDING_CREATE);
  const canEditBuilding = can(permissions, PERMISSIONS.BUILDING_EDIT);
  const canReadBuilding = can(permissions, PERMISSIONS.BUILDING_READ);

  // Told apart from "the list is empty": an unknown ?edit= must be dismissed
  // once, and only once the list has actually answered.

  const [editableBuildingId, setEditableBuildingId] = useState<string | null>(
    null
  );
  const [buildingToBeDeleted, setBuildingToBeDeleted] =
    useState<BuildingData | null>(null);
  const [isActionModalVisible, setIsActionModalVisible] =
    useState<boolean>(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] =
    useState<boolean>(false);

  const [searchParam, setSearchParam] = useState('');
  const [portfolioFilter, setPortfolioFilter] = useState('');
  // Same primitive dashboard-billing.tsx:58 already uses for '?checkout='.
  const [params, setParams] = useSearchParams();

  // The org's portfolios, for the filter. `allowed` is false for a concierge,
  // who never receives portfolio_name on these rows either — the filter is
  // hidden rather than rendered empty.
  const { portfolios, allowed: canFilterByPortfolio } = usePortfolios();
  const canReadPortfolios = can(permissions, PERMISSIONS.PORTFOLIO_READ);

  /**
   * THE BUILDING LIST — THROUGH useResource, so a failure cannot masquerade as
   * an empty account.
   *
   * This used to be a bare `useEffect` with no try/catch and no loading flag.
   * `buildingsData` started as `[]`, so the screen painted "No properties yet —
   * add your first building" BEFORE the request resolved, and if the request
   * REJECTED that empty state was the screen's permanent, final answer: no
   * error, no banner, no retry. An owner with forty buildings was told they had
   * none, and the only affordance on offer was to re-create them — which spends
   * prepaid units and makes duplicates.
   *
   * `enabled: Boolean(user)` rather than an early return: before the user
   * lands there is nothing to ask, and claiming to be loading (or claiming to
   * be empty) would both be untrue.
   */
  /* ── THE DELETED VIEW + RESTORE (QA-09, owner decision 2026-08-28) ─────────
   *
   * Deleting a building is a soft delete: the row, its units, its occupancies
   * and its audit trail all survive, and PUT /dashboard/buildings has always
   * accepted status:'active' to bring it back — gated behind assertUnitAllowance
   * (properties.js), because reactivating re-enters every billable unit into the
   * meter and an unguarded flag flip would be a free-unit generator. What was
   * missing was any screen that could list a deleted building or ask for that.
   */
  const [lifecycle, setLifecycle] = useState<'active' | 'inactive'>('active');
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const buildings = useResource<BuildingData[]>(
    async () => {
      if (!user) return [];

      // The deleted view is owner-only: it is a server-side narrowing that only
      // the owner's GET branch can ask for, so `lifecycle` is never 'inactive'
      // for a caller taking the POST branch and the control is not rendered for
      // them.
      const response =
        normalizeRole(user?.role) === ROLES.OWNER
          ? await fetchAllItems(
              'dashboard/buildings',
              undefined,
              lifecycle === 'inactive' ? { status: 'inactive' } : undefined
            )
          : await createItem('dashboard/buildings', user);

      return (response || []) as BuildingData[];
    },
    {
      enabled: Boolean(user),
      fallbackMessage: 'We could not load your buildings.',
      // The two views are different REQUESTS, not two filters over one payload.
      deps: [lifecycle],
    }
  );

  const buildingsData = buildings.data || [];
  // "The question has been answered", which is what the deep-link cleanup below
  // waits on. A FAILED load answers it too — otherwise a stale ?edit=<id> spins
  // forever against a list that is never coming.
  const buildingsLoaded = !buildings.isLoading && Boolean(user);
  const getPropertiesfromDB = buildings.reload;

  // ONE visible array, both predicates applied, fed straight to usePagination.
  // Filtering `displayedData` instead would filter an already-paginated slice:
  // short pages, a page count taken from the unfiltered list, and blank pages
  // past the first. Deriving it also means the list follows a refetch instead of
  // sitting on a stale copy taken at the moment of the last keystroke.
  const visibleBuildings = useMemo(() => {
    const searchTerm = searchParam.toLowerCase();

    return (buildingsData || []).filter((building: BuildingData) => {
      const matchesSearch =
        !searchTerm ||
        building.property_name?.toLowerCase().includes(searchTerm) ||
        building.address?.toLowerCase().includes(searchTerm) ||
        building.city?.toLowerCase().includes(searchTerm) ||
        building.email[0]?.toLowerCase().includes(searchTerm);

      // The select's value is a string; portfolio_id arrives as a number.
      const matchesPortfolio =
        !portfolioFilter || String(building.portfolio_id) === portfolioFilter;

      return Boolean(matchesSearch) && matchesPortfolio;
    });
  }, [buildingsData, searchParam, portfolioFilter]);

  // Buildings nobody has placed on the map: their zone is the browser's at the
  // time they were added, or the column default. A row from an API that does
  // not send `timezone_source` is not counted — absence is not evidence.
  const unplacedCount = useMemo(
    () =>
      (buildingsData || []).filter(
        (building: BuildingData) =>
          (building.timezone_source === 'default' ||
            building.timezone_source === 'browser') &&
          (building.latitude === null || building.latitude === undefined)
      ).length,
    [buildingsData]
  );

  const {
    currentPage,
    displayedData,
    nextPage,
    previousPage,
    hasPreviousPage,
    hasNextPage,
    getTotalPages,
    resetPagination,
  } = usePagination(visibleBuildings);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchParam(event.target.value);
    resetPagination();
  };

  const handlePortfolioFilterChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setPortfolioFilter(event.target.value);
    resetPagination();
  };

  const showAllPortfolios = () => {
    setPortfolioFilter('');
    resetPagination();
  };

  // Which filters are narrowing the list right now — the empty state has to say
  // "nothing matched" rather than "you have no properties".
  const isFiltered = Boolean(searchParam || portfolioFilter);
  const filteredPortfolioName = portfolios.find(
    portfolio => String(portfolio.id) === portfolioFilter
  )?.name;

  // An owner with forty buildings must never be told they have none simply
  // because a portfolio filter is on, so each combination gets its own line.
  const emptyStateMessage = () => {
    const portfolioName = filteredPortfolioName || 'this portfolio';

    if (portfolioFilter && searchParam) {
      return `No properties in ${portfolioName} match that search.`;
    }

    if (portfolioFilter) {
      return `There are no properties in ${portfolioName} yet.`;
    }

    if (searchParam) {
      return 'Try a different name, city, or address.';
    }

    return 'Add your first building to start welcoming guests.';
  };

  const totalPages = getTotalPages();
  const navigate = useNavigate();

  // Absolute, not relative. The dashboard is mounted on the splat route
  // '/dashboard/*' (app-router.tsx), so a relative navigate() resolves against
  // the MATCHED path — from /dashboard/buildings, 'buildings/add-new-property'
  // became /dashboard/buildings/buildings/add-new-property, which matches no
  // case in dashboard.tsx's pathname switch and rendered nothing.
  const openAddNewBuildingForm = () => {
    navigate('/dashboard/buildings/add-new-property');
  };


  // ─────────────────────  The edit modal, addressed by URL  ─────────────────
  //
  // No by-id endpoint is involved: the building is taken from the list this
  // screen has already fetched, which is the same payload the old page received
  // through React state. There is no GET /dashboard/buildings/:id and none is
  // needed.
  const editId = params.get(EDIT_PARAM);
  const editingBuilding = editId
    ? (buildingsData || []).find(building => String(building.id) === editId)
    : undefined;

  // Written with the CURRENT params rather than a fresh object so a param this
  // screen does not know about survives being edited.
  const setEditParam = (buildingId: string | null) => {
    const next = new URLSearchParams(params);

    if (buildingId === null) {
      next.delete(EDIT_PARAM);
    } else {
      next.set(EDIT_PARAM, buildingId);
    }

    // Deliberately NOT { replace: true }: opening the modal is a history entry,
    // so the browser Back button closes it instead of leaving the dashboard.
    setParams(next);
  };

  const openBuilding = (building: BuildingData) => {
    // A role without building:read has no clickable row at all; this is the
    // second gate, for the row-action Edit item and anything added later.
    if (!canReadBuilding) return;

    hideEditDeleteActions();
    setEditParam(String(building.id));
  };

  const closeBuilding = () => {
    setEditParam(null);
  };

  // A unit was added or saved inside the modal. The row's "N units" column is
  // units_count, computed server-side over status='active' AND
  // kind <> 'unassigned' (properties.js:295-317), so it is stale from that
  // moment on — but the refetch is DEFERRED to when the modal closes rather
  // than run immediately, and that is not laziness:
  //
  //   getPropertiesfromDB() replaces buildingsData, so `editingBuilding` becomes
  //   a NEW object; DashboardEditBuildingById keys three effects on that prop
  //   identity, and two of them RE-SEED the form (the map location and the
  //   property email). Refetching under an open modal would silently throw away
  //   a location the user had just picked and had not saved yet.
  //
  // Nothing is lost by waiting: units_count is only ever visible on the list,
  // which is behind the modal until it closes.
  const unitsChangedRef = useRef(false);

  const handleUnitsChanged = () => {
    unitsChangedRef.current = true;
  };

  // Covers EVERY way the modal goes away — Cancel, the scrim, Escape, and the
  // browser Back button, which drops the param without calling closeBuilding().
  useEffect(() => {
    if (editId || !unitsChangedRef.current) return;

    unitsChangedRef.current = false;
    void getPropertiesfromDB().catch(() => undefined);
  }, [editId]);

  const handleBuildingSaved = async () => {
    // Its own refetch covers the unit changes too, so the effect above must not
    // fire a second one when the param drops.
    unitsChangedRef.current = false;

    // The list is still mounted behind the modal, so it no longer refetches by
    // remounting the way it did when editing was a separate page. Without this
    // the edited row keeps the values it was rendered with.
    //
    // Swallowed on purpose: the PUT already succeeded and said so. A failed
    // REFRESH must not trap the user inside a modal over a stale row.
    await getPropertiesfromDB().catch(() => undefined);
    closeBuilding();
  };

  // An ?edit= that names nothing this user can see — a stale bookmark, a
  // building since deleted, or another org's id. Clear it once the list has
  // answered, otherwise the spinner shell would sit there forever.
  useEffect(() => {
    if (!editId || !buildingsLoaded || editingBuilding) return;

    toast.error('That building is not in your list any more.');
    setEditParam(null);
  }, [editId, buildingsLoaded, editingBuilding]);

  const showEditDeleteActions = (buildingId: string) => {
    // Toggle only when the SAME row's trigger is clicked again. Blindly
    // inverting the flag meant opening row B while row A was open simply closed
    // the menu, so the second building you tried always appeared unclickable.
    const isSameRow = isActionModalVisible && editableBuildingId === buildingId;
    if (isSameRow) {
      hideEditDeleteActions();

      return;
    }

    const building = buildingsData?.find(b => b.id === buildingId);

    if (!building) return;

    setIsActionModalVisible(true);
    setEditableBuildingId(buildingId);
    setBuildingToBeDeleted(building);
  };

  // CLOSING THE MENU AND FORGETTING WHICH BUILDING IT WAS ARE TWO DIFFERENT ACTS.
  //
  // They used to be one function, and that is the whole of QA-01 ("Building
  // deletion is a silent no-op", 2026-08-27). The sequence:
  //
  //   1. the row menu opens and records buildingToBeDeleted,
  //   2. "Delete" in that menu opens the confirm modal — and leaves the menu
  //      open, so the outside-click listener below is still attached,
  //   3. the modal is portalled to <body>, so pressing its "Delete building"
  //      button fires `mousedown` on a target that is not inside the menu,
  //   4. the listener therefore ran, and cleared buildingToBeDeleted,
  //   5. `click` then reached handleConfirmDelete with nothing to delete, and
  //      its `if (buildingToBeDeleted)` quietly did nothing.
  //
  // No request, no error, no console output, and the dialog closed looking
  // successful — which is exactly what was reported, and why revoking the
  // building's last staff grant "did not help": it was never authorisation.
  //
  // Verified against the API first (scripts/wp/repro-qa01-building-delete.js in
  // the backend repo): PUT /dashboard/buildings flips the status, writes its
  // audit row and drops the building from the list. The server was never the
  // problem, so nothing here may be "fixed" by retrying harder.
  const closeActionMenu = () => {
    setIsActionModalVisible(false);
    setEditableBuildingId(null);
  };

  const hideEditDeleteActions = () => {
    closeActionMenu();
    setBuildingToBeDeleted(null);
  };

  // Dismiss the row action menu on an outside click or Escape.
  //
  // This deliberately replaces the full-screen backdrop <div> that used to do
  // the job. A backdrop cannot win here: rendered inside the row it inherits the
  // row's transform as its containing block and covers only the row, and
  // portalled to <body> it paints above the menu itself — because every
  // ancestor from #dashboard-buildings-content down runs an entry animation with
  // fill-mode 'both', leaving a transform that creates a stacking context the
  // menu's z-index cannot escape. A listener has no stacking context at all.
  useEffect(() => {
    // ONCE THE CONFIRM MODAL IS UP, NOTHING IS "OUTSIDE" ANY MORE. The modal is
    // portalled, so every click in it looks external to this menu. Standing down
    // while it is open is the first of the two guards against QA-01; the second
    // is that these handlers no longer clear the pending target at all.
    if (!isActionModalVisible || isDeleteModalVisible) return;

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;

      // Clicks on the menu itself, or on the trigger (which toggles), are not
      // "outside" — closing here would unmount the button before its click
      // event could fire, which is what made Delete and Edit look inert.
      if (target?.closest('[data-action-menu], [data-action-trigger]')) return;

      closeActionMenu();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeActionMenu();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isActionModalVisible, isDeleteModalVisible]);

  const handleDeleteButtonClick = () => {
    setIsDeleteModalVisible(true);
  };

  const handleCancelDelete = () => {
    // Closes the ROW MENU as well, not just the dialog. Clearing the target
    // while leaving the menu open would arm the failure the guard in
    // handleConfirmDelete now catches: press Delete again and the confirm has
    // nothing to act on. Cancel ends the whole interaction, so it ends all of it.
    setIsDeleteModalVisible(false);
    hideEditDeleteActions();
  };

  const handleConfirmDelete = async () => {
    // A MISSING TARGET IS A FAULT, NOT A NO-OP.
    //
    // This was `if (buildingToBeDeleted) { …request… }` followed unconditionally
    // by a refetch and a clean close, so losing the target skipped the request
    // and still looked like a completed delete. Whatever else goes wrong here,
    // the one outcome that must never happen again is a destructive action that
    // reports success without asking the server anything — so this branch says
    // so and leaves the dialog open rather than closing on a lie.
    if (!buildingToBeDeleted) {
      toast.error('Could not tell which building to delete. Please try again.');

      return;
    }

    const deletedName = buildingToBeDeleted.property_name || 'That building';

    try {
      await updatePropertyOrUserItem('dashboard/buildings', {
        ...buildingToBeDeleted,
        status: 'inactive',
      });

      await getPropertiesfromDB();
      setIsDeleteModalVisible(false);
      hideEditDeleteActions();
      // Said out loud. A silent success and a silent failure look identical, and
      // for a whole QA pass they were: the report reads "gives no success or
      // error status" because there was nothing to see either way.
      toast.success(`${deletedName} deleted.`);
    } catch {
      toast.error('Could not delete that building. Please try again.');
    }
  };

  /* RESTORE (QA-09). The server already had this: PUT with status:'active' runs
   * assertUnitAllowance first, because every billable unit in the building
   * re-enters the meter on reactivation. So a restore that would put the
   * organisation over its prepaid cap is REFUSED with a 403 carrying
   * code:'UNIT_LIMIT' and its own sentence — which is the one error here that
   * must reach the reader verbatim rather than as "please try again", because it
   * tells them exactly what to buy. */
  const handleRestore = async (building: BuildingData) => {
    if (restoringId) return;
    setRestoringId(String(building.id));
    try {
      await updatePropertyOrUserItem('dashboard/buildings', {
        ...building,
        status: 'active',
      });
      await getPropertiesfromDB();
      toast.success(`${building.property_name || 'That building'} restored.`);
    } catch (error) {
      const shaped = error as {
        response?: { data?: { message?: string; code?: string } };
      };
      const server = shaped?.response?.data;
      toast.error(
        server?.code === 'UNIT_LIMIT' && server.message
          ? server.message
          : 'Could not restore that building. Please try again.'
      );
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <DashboardContent id="dashboard-buildings-content">
      <BuildingsListing>
        <Title>
          <Text
            mt={0}
            mb={0}
            fontSize={18}
            fontWeight={700}
            color={colors.grey_dark}
          >
            Buildings
          </Text>
          <Text mt={0} mb={0} fontSize={13} color={`${colors.grey}`}>
            Please select property to show
          </Text>
        </Title>
        {/* ONE GROUP, so the row wraps under the title instead of squeezing
            it (dashboard.styled.tsx, ListingControls). */}
        <ListingControls>
          <ListingSearch>
            {/* An accessible name, and the RIGHT one. The only text describing
                this box was a placeholder — which vanishes the moment anything is
                typed, and is not a label — above a commented-out "Search:" that
                had been dead for long enough to be forgotten. The buildings list's box
                also carried name="userSearch". */}
            <StyledInput
              name="buildingSearch"
              type="search"
              aria-label="Search buildings"
              value={searchParam}
              onChange={handleSearchChange}
              placeholder="Search..."
            />
          </ListingSearch>
          {/* ACTIVE / DELETED (QA-09). Owner-only for the same reason the People
              screen's is: the inactive list is a server-side narrowing only the
              owner's GET branch can ask for. */}
          {normalizeRole(user?.role) === ROLES.OWNER && (
            <PortfolioFilterContainer>
              <StyledBuildingSelect
                id="buildings-lifecycle-filter"
                aria-label="Show active or deleted buildings"
                name="lifecycleFilter"
                value={lifecycle}
                onChange={event =>
                  setLifecycle(event.target.value as 'active' | 'inactive')
                }
              >
                <option value="active">Active buildings</option>
                <option value="inactive">Deleted buildings</option>
              </StyledBuildingSelect>
            </PortfolioFilterContainer>
          )}
          {canFilterByPortfolio && (
            <PortfolioFilterContainer>
              <StyledBuildingSelect
                id="buildings-portfolio-filter"
                aria-label="Filter by portfolio"
                name="portfolioFilter"
                value={portfolioFilter}
                onChange={handlePortfolioFilterChange}
              >
                <option value="">All portfolios</option>
                {portfolios.map(portfolio => (
                  <option key={portfolio.id} value={String(portfolio.id)}>
                    {portfolio.is_default
                      ? `${portfolio.name} (default)`
                      : portfolio.name}
                  </option>
                ))}
              </StyledBuildingSelect>
            </PortfolioFilterContainer>
          )}
          {canCreateBuilding && (
            <NewBuildingButton
              id="new-building-button"
              type="button"
              onClick={() => openAddNewBuildingForm()}
            >
              Add new building
            </NewBuildingButton>
          )}
        </ListingControls>
      </BuildingsListing>

      {/* A building's times are shown on the building's own clock, and that
          clock is taken from where the building is. One that has never been
          placed on the map is still on a zone nobody confirmed — the browser's
          at the time it was added, or the default. Said once, here, to the
          people who can fix it. */}
      {canEditBuilding && lifecycle === 'active' && unplacedCount > 0 && (
        <ListingNotice role="status">
          {unplacedCount === 1
            ? 'One building is not on the map yet, so its time zone has not been confirmed.'
            : `${unplacedCount} buildings are not on the map yet, so their time zones have not been confirmed.`}{' '}
          Open the building, choose Edit, and pin it on the map.
        </ListingNotice>
      )}

      <Column>

        {/* THREE STATES, THREE RENDERS — and in this order.
            "No properties yet" is only true once we have asked AND been
            answered. Before that it is a guess, and after a failure it is a
            falsehood whose only affordance re-creates buildings that exist.

            ONE BOX, FOUR FILLS. StateSwitch crossfades skeleton, error, empty
            and table inside the same container with a reserved floor, so the
            page never jumps between them. A REFETCH is not a state change:
            isLoading is the first load only, so a reload keeps the table and
            Staleable dims it until the new rows land. */}
        <StateSwitch
          state={
            buildings.isLoading
              ? 'loading'
              : buildings.error
                ? 'error'
                : displayedData.length === 0
                  ? 'empty'
                  : 'ready'
          }
          minHeight={skeletonHeight(SKELETON_ROWS, ROW_HEIGHT)}
        >
          {buildings.isLoading ? (
            <ListSkeleton
              rows={SKELETON_ROWS}
              rowHeight={ROW_HEIGHT}
              label="buildings"
            />
          ) : buildings.error ? (
            <LoadError message={buildings.error} onRetry={buildings.reload} />
          ) : displayedData.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.6}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 21V8a7 7 0 0 1 14 0v13" />
                  <path d="M3 21h18" />
                  <path d="M14.5 12.5v1" />
                </svg>
              </EmptyStateIcon>
              <Text
                fontSize={20}
                fontWeight={700}
                mt={0}
                mb={0}
                color={colors.grey_dark}
              >
                {isFiltered ? 'No matches found' : 'No properties yet'}
              </Text>
              <Text
                fontSize={14}
                mt={0}
                mb={0}
                color={colors.grey}
                align="center"
                style={{ display: 'block', maxWidth: '320px' }}
              >
                {emptyStateMessage()}
              </Text>
              {portfolioFilter && (
                <CancelButton
                  id="empty-show-all-portfolios"
                  type="button"
                  onClick={() => showAllPortfolios()}
                >
                  <ButtonTitle>Show all portfolios</ButtonTitle>
                </CancelButton>
              )}
              {canCreateBuilding && !isFiltered && (
                <NewBuildingButton
                  id="empty-add-building"
                  type="button"
                  onClick={() => openAddNewBuildingForm()}
                >
                  Add new building
                </NewBuildingButton>
              )}
            </EmptyState>
          ) : (
            <Staleable stale={buildings.isRefreshing}>
              <TableFrame>
                <Table>
                  <TableCaption>
                    Buildings in this organisation, where they are, how they
                    are staffed and how many units they hold
                  </TableCaption>
                  <Thead>
                    <Tr>
                      <Th width={22}>Property name</Th>
                      <Th width={26}>City</Th>
                      {/* Independent Stays IS1: no front desk without Lobbero Desk. */}
                      {desk ? <Th width={23}>Front desk</Th> : null}
                      <Th width={23}>Units</Th>
                      {/* MR-23. This was "Action" in a 6% column, uppercased
                          at 12px with 0.08em tracking into roughly 34px of
                          text box, then ellipsised by a rule that applied to
                          every cell on this screen — with no title and no
                          other exposure of the word. The engine's Th wraps
                          rather than truncating: a two-line header costs one
                          row of height once, a truncated one costs the reader
                          the column's name on every visit. */}
                      {canEditBuilding && <Th width={6}>Action</Th>}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {/* ROWS ARRIVE IN A CASCADE AND LEAVE WHEN DELETED. Keyed
                        by id inside a Presence, a deleted building animates
                        out and the rows under it slide up (`layout`). The
                        Presence is keyed by page and filter so turning a page
                        or narrowing the list swaps the set instantly and
                        replays the cascade, rather than stacking twelve rows
                        while six of them fade. */}
                    <Presence
                      key={`${currentPage}:${searchParam}:${portfolioFilter}:${lifecycle}`}
                    >
                      {displayedData.map((e, i) => {
                        /* WP-37 — THE ROW IS A ROW AGAIN, AND THE BUTTON IS A
                           BUTTON. This carried role="button", tabIndex and its
                           own Enter/Space handler, which was the only way to
                           make a div row operable. On a tr that role would
                           REPLACE the row role, so the table would assert one
                           button where a row belongs — precisely what this
                           package exists to stop. The affordance moves onto
                           the building's NAME, a real button with the same
                           accessible name the row had: the keyboard user gets
                           a proper control, the pointer user keeps the
                           whole-row click. */
                        return (
                          <BodyRow
                            key={e.id}
                            index={i}
                            onClick={
                              canReadBuilding ? () => openBuilding(e) : undefined
                            }
                          >
                            <Td data-label="Property name">
                              <CellFlex>
                                <CoverThumb src={e.cover_url} />

                                <BuildingNameStack>
                                  {canReadBuilding ? (
                                    <BuildingNameButton
                                      type="button"
                                      aria-label={`Open ${e.property_name}`}
                                      onClick={(event: React.MouseEvent) => {
                                        // The row also opens this building;
                                        // without stopping here it would run
                                        // twice.
                                        event.stopPropagation();
                                        openBuilding(e);
                                      }}
                                    >
                                      {e.property_name}
                                    </BuildingNameButton>
                                  ) : (
                                    <Text
                                      mt={0}
                                      mb={0}
                                      fontSize={14}
                                      fontWeight={400}
                                      color={colors.grey_dark}
                                    >
                                      {e.property_name}
                                    </Text>
                                  )}
                                  {canReadPortfolios && (
                                    <BuildingPortfolioSubtitle>
                                      {e.portfolio_name || '—'}
                                    </BuildingPortfolioSubtitle>
                                  )}
                                </BuildingNameStack>
                              </CellFlex>
                            </Td>

                            <Td data-label="City">
                              <Text
                                mt={0}
                                mb={0}
                                fontSize={14}
                                fontWeight={400}
                                color={colors.grey_dark}
                              >
                                {e.city || '—'}
                              </Text>
                            </Td>
                            {/* "Not set", not an em dash. QA-14, 2026-08-27.
                                Staffing is genuinely optional, so an unset
                                building is ordinary — but "—" is the same glyph
                                this product uses for "no value to show", and a
                                reader cannot tell an absent SETTING from an
                                absent FACT. The word says which it is; the
                                muted colour keeps it reading as an absence
                                rather than as a staffing model called "Not
                                set". */}
                            {desk ? (
                              <Td data-label="Front desk">
                                <Text
                                  mt={0}
                                  mb={0}
                                  fontSize={14}
                                  fontWeight={400}
                                  color={
                                    STAFFING_LABELS[e.staffing_code || '']
                                      ? colors.grey_dark
                                      : colors.grey
                                  }
                                >
                                  {STAFFING_LABELS[e.staffing_code || ''] ||
                                    'Not set'}
                                </Text>
                              </Td>
                            ) : null}

                            <Td data-label="Units">
                              <Text
                                mt={0}
                                mb={0}
                                fontSize={14}
                                fontWeight={400}
                                color={colors.grey_dark}
                              >
                                {e.units_count ?? 0} unit
                                {e.units_count === 1 ? '' : 's'}
                              </Text>
                            </Td>

                            {/* A DELETED BUILDING GETS ONE ACTION. Edit and
                                Delete are meaningless on a building that is
                                already gone, and the row-actions menu is where
                                QA-01 lived — offering it over a list where
                                nothing can be deleted is pure risk. */}
                            {lifecycle === 'inactive' && canEditBuilding && (
                              <Td
                                data-label="Action"
                                style={{ textAlign: 'right' }}
                              >
                                <RestoreBuildingButton
                                  type="button"
                                  disabled={restoringId === String(e.id)}
                                  aria-label={`Restore ${e.property_name}`}
                                  onClick={(event: React.MouseEvent) => {
                                    // The row opens the building; a restore
                                    // must not.
                                    event.stopPropagation();
                                    void handleRestore(e);
                                  }}
                                >
                                  {restoringId === String(e.id)
                                    ? 'Restoring…'
                                    : 'Restore'}
                                </RestoreBuildingButton>
                              </Td>
                            )}

                            {lifecycle === 'active' && canEditBuilding && (
                              <Td
                                data-label="Action"
                                style={{ textAlign: 'right' }}
                              >
                                {/* The row itself opens the property now, so
                                    the trigger has to stop its click there —
                                    without this, one tap on the three dots
                                    opened the menu AND the modal on top of it.
                                    onClick is enough: the outside-click
                                    dismissal listens for mousedown, and it
                                    already ignores [data-action-trigger] by
                                    hand. */}
                                <RowActionTrigger
                                  type="button"
                                  data-action-trigger
                                  aria-label={`Actions for ${e.property_name}`}
                                  aria-expanded={
                                    isActionModalVisible &&
                                    editableBuildingId === e.id
                                  }
                                  onClick={event => {
                                    event.stopPropagation();
                                    showEditDeleteActions(e.id);
                                  }}
                                >
                                  <ThreeDots />
                                </RowActionTrigger>

                                {/* THE MENU GROWS FROM ITS TRIGGER AND SHRINKS
                                    BACK. Inside a Presence so dismissal plays
                                    the exit; overlayMotion('popover') with the
                                    origin at the top-right corner the trigger
                                    sits in. The entrance is a transform and an
                                    opacity — nothing that blocks a pointer, so
                                    the e2e run that clicks Delete with no wait
                                    lands on a live button. */}
                                <Presence>
                                  {isActionModalVisible &&
                                  editableBuildingId === e.id ? (
                                    /* Same reason as the trigger: the menu sits
                                       INSIDE the row, so Delete and Edit would
                                       both reach the row's handler. The
                                       outside-click listener already treats
                                       [data-action-menu] as inside, so
                                       swallowing mousedown here changes nothing
                                       about dismissal. */
                                    <ActionModal
                                      key="menu"
                                      {...overlayMotion('popover')}
                                      style={{ transformOrigin: 'top right' }}
                                      data-action-menu
                                      role="group"
                                      aria-label={`Actions for ${e.property_name}`}
                                      onClick={event => event.stopPropagation()}
                                      onMouseDown={event =>
                                        event.stopPropagation()
                                      }
                                    >
                                      {/* EDIT FIRST, DEACTIVATE LAST. The
                                          destructive action was the FIRST item
                                          in a horizontal strip, so it sat
                                          directly under the menu trigger and
                                          took the focus and the thumb that
                                          were aimed at the common action. The
                                          safe, frequent one leads; the
                                          consequential one is at the far end
                                          where reaching it is deliberate. */}
                                      <ButtonContainer
                                        type="button"
                                        onClick={() => openBuilding(e)}
                                      >
                                        <ButtonTitle>Edit</ButtonTitle>
                                      </ButtonContainer>
                                      <ButtonContainer
                                        type="button"
                                        isDelete={true}
                                        onClick={handleDeleteButtonClick}
                                      >
                                        {/* "Delete" — one destructive verb
                                            across every object (R-14). This
                                            was the only row action in the
                                            product that said Deactivate. */}
                                        <ButtonTitle>Delete</ButtonTitle>
                                      </ButtonContainer>
                                    </ActionModal>
                                  ) : null}
                                </Presence>
                              </Td>
                            )}
                          </BodyRow>
                        );
                      })}
                    </Presence>
                  </Tbody>
                </Table>
              </TableFrame>
            </Staleable>
          )}
        </StateSwitch>

        {displayedData.length > 0 && (
          <PaginationButtons
            currentPage={currentPage}
            hasPreviousPage={hasPreviousPage}
            hasNextPage={hasNextPage}
            previousPage={previousPage}
            nextPage={nextPage}
            totalPages={totalPages}
          />
        )}
      </Column>

      {/* Mounted CONDITIONALLY and keyed on the building id. Both matter: the
          form seeds its state from props once, so a long-lived instance would
          show the previous building's values, and unmounting is what resets it.
          The key also means opening a second property never reuses the first
          one's state.

          Inside a Presence, so closing plays the modal's exit (the scrim fades,
          the card shrinks back) before the portal unmounts — and the loading
          shell hands over to the form with the same key discipline. */}
      <Presence>
        {canReadBuilding && editId ? (
          editingBuilding ? (
            <DashboardEditBuildingById
              key={editingBuilding.id}
              editableBuildingData={editingBuilding}
              canEdit={canEditBuilding}
              onClose={closeBuilding}
              onSaved={handleBuildingSaved}
              onUnitsChanged={handleUnitsChanged}
            />
          ) : (
            <BuildingLoadingDialog key="building-loading" onClose={closeBuilding} />
          )
        ) : null}
      </Presence>

      <ConfirmDialog
        open={isDeleteModalVisible}
        title="Delete building"
        /* THE COPY STATES WHAT THE BUTTON DOES, under the one destructive verb
           R-14 standardises on. The handler PUTs status: 'inactive' — a soft
           delete — so the retention statement is not decoration: the records
           really are kept, and there is no screen in the product that lists or
           restores an inactive building, which is why the no-undo line stays
           too. An overstated warning teaches people to disbelieve the next one;
           an understated consequence is worse. This states both. */
        message={`${
          buildingToBeDeleted?.property_name || 'This property'
        } will be deleted and will disappear from your lists, reports and pickers. Its records are kept in the audit log, but this cannot be undone from inside the app — ask support if you need it back.`}
        primaryLabel="Delete building"
        primaryVariant="destructive"
        dismissLabel="Cancel"
        onPrimary={handleConfirmDelete}
        onDismiss={handleCancelDelete}
      />
    </DashboardContent>
  );
};
